import { inject, Injectable } from '@angular/core';

import { BridgeAPIService } from 'app/providers/bridge-api.service';
import { LoggerService } from 'app/services/logger.service';
import { EnableClassTelemetry } from 'app/services/otel.service';

import { Subscribable } from 'app/utils/subscrible';
import { OriginTypes } from '../semantria-api/semantria-account.service';
import { Session } from './auth.service';

export interface AccountBilling {
  app_seats_allocated: number;
  app_seats_permitted: number;
  data_calls_balance: number;
  data_calls_limit: number;
  data_calls_limit_interval: number;
  docs_balance: number;
  docs_suggested: number;
  docs_suggested_interval: number;
  expiration_date: Date;
  job_ids_allocated: number;
  job_ids_permitted: number;
  limit_type: string;
  polling_calls_balance: number;
  polling_calls_limit: number;
  polling_calls_limit_interval: number;
  priority: string;
  settings_calls_balance: number;
  settings_calls_limit: number;
  settings_calls_limit_interval: number;
}

export interface AccountLimits {
  auto_response_limit: number;
  batch_limit: number;
  blacklist_limit: number;
  callback_batch_limit: number;
  categories_limit: number;
  category_samples_limit: number;
  characters_limit: number;
  collection_limit: number;
  configurations_limit: number;
  entities_limit: number;
  metadata_limit: number;
  near_operator_distance: number;
  near_operator_limit: number;
  output_data_limit: number;
  processed_batch_limit: number;
  queries_limit: number;
  return_source_text: boolean;
  sentiment_limit: number;
  taxonomy_depth_limit: number;
  taxonomy_limit: number;
  taxonomy_topics_limit: number;
  SPOTLIGHT_MAX_PAGES_PER_DASHBOARD: number;
  SPOTLIGHT_MAX_WIDGETS_PER_PAGE: number;
}

export interface AccountTemplate {
  config_id: string;
  id: string;
  is_free: boolean;
  language: string;
  name: string;
  type: string;
  version: string;
}

export interface Account {
  id: string;
  name: string;
  email_address: string;
  is_active: boolean;
  supported_languages: string[];
  billing?: AccountBilling;
  limits?: AccountLimits;
  features?: string[];
  current_translation_balance?: number;
  balances?: {
    current_translation_balance: number;
    DRIVER_ANALYSES: number;
    SPOT_E_SCENARIO: number;
    [field: string]: number;
  };
  templates?: AccountTemplate[];
  storageLimits: {
    [field: string]: number;
  };
  originNames: OriginTypes[];
  external_resources?: ExternalRule[];
}

export interface Consumer {
  _: any;
}

export interface Keypair {
  _: any;
}

export interface PasswordRule {
  rule_type: string;
  value: string;
}

export interface PasswordRules {
  rules: PasswordRule[];
}

export interface ExternalRule {
  external_type: string;
  external_id: string;
  external_name: string;
}

@EnableClassTelemetry()
@Injectable()
export class SemantriaAccount extends BridgeAPIService {
  private readonly logger = inject(LoggerService);
  public sub = new Subscribable<Account>(this.getAccount.bind(this));

  public static mapAccount(account: Account): Account {
    account.billing.expiration_date = new Date(account.billing.expiration_date);
    return account;
  }

  public static mapConsumer(consumer: Consumer): Consumer {
    return consumer;
  }

  public async getAccount(): Promise<Account> {
    const result = await this.get<Account>('/account');
    const mapped = SemantriaAccount.mapAccount(result);
    return mapped;
  }

  public async getPasswordRules(): Promise<PasswordRules> {
    return this.get<PasswordRules>('/account/password-rules');
  }

  public async changeUserPassword(userId: string, password: string): Promise<any> {
    return this.put<any>('/account/_reset_password', { userId, password });
  }

  public async updatePassword(existingPassword: string, newPassword: string): Promise<Session> {
    return this.put<Session>('/session/user/password', { existingPassword, newPassword });
  }

  public async hasAccountFeature(feature: string): Promise<boolean> {
    return this.sub.latest().then((acc) => acc.features.includes(feature));
  }

  public async hasAccountExternalResource(resource: string): Promise<boolean> {
    try {
      const acc = await this.sub.latest();
      return acc.external_resources?.some((rule) => rule.external_id === resource) ?? false;
    } catch (error) {
      this.logger.error('Error fetching account:', { error });
      return false;
    }
  }
}
